/**
 * Public privacy policy page (no auth) — required by Google Play + App Store.
 * Route: /privacy
 */
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";

const H2 = ({ children }) => (
  <h2 className="font-serif text-xl sm:text-2xl text-[var(--inmind-text)] mt-10 mb-3 tracking-tight">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="font-medium text-base text-[var(--inmind-text)] mt-6 mb-2">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-[var(--inmind-text-secondary)] leading-relaxed mb-3 text-[15px]">{children}</p>
);
const Li = ({ children }) => <li className="mb-1.5 leading-relaxed">{children}</li>;

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[var(--inmind-bg)] text-[var(--inmind-text)]" data-testid="privacy-page">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-home-link">
            <Logo className="w-6 h-6" />
            <span className="font-serif text-lg text-[var(--inmind-text)]">InThink</span>
          </Link>
          <Link to="/support" className="text-sm text-[var(--inmind-primary)] hover:underline">Support</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">Legal</div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-2 tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-[var(--inmind-text-secondary)]">
          Effective: 13 August 2026 · Last updated: 13 August 2026
        </p>

        <div className="mt-8 text-[15px] text-[var(--inmind-text-secondary)] leading-relaxed">
          <P>
            InThink (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a daily learning app that helps you sit
            with one thoughtful concept a day, keep private notes, and think through ideas with an AI
            helper called &quot;Think&quot;. This policy explains what data we collect, how we use it,
            and the choices you have.
          </P>
          <P>
            If you have any questions, email{" "}
            <a href="mailto:chaudharyhardik1106@gmail.com" className="text-[var(--inmind-primary)] underline">
              chaudharyhardik1106@gmail.com
            </a>.
          </P>

          <H2>1. What we collect</H2>
          <P>We only collect what we need to run the service.</P>
          <ul className="list-disc list-outside pl-5 text-[var(--inmind-text-secondary)] text-[15px] leading-relaxed">
            <Li><b>Account</b> — your email, name (if you provide one), a bcrypt-hashed password (never the plain text), the sign-in providers you use (email/password and/or Google), account creation date, and last sign-in date.</Li>
            <Li><b>App content you create</b> — personal notes, daily cards shown to you, your review status on cards, and messages you send to the &quot;Think&quot; AI helper. This content is yours.</Li>
            <Li><b>Preferences</b> — subject categories, timezone, and display settings.</Li>
            <Li><b>Usage data (limited)</b> — server logs and short-lived rate-limit counters (e.g. how many Think messages you sent today) so we can prevent abuse.</Li>
          </ul>
          <P className="mt-3">
            We do <b>not</b> collect: location, contacts, phone number, camera, microphone, or advertising identifiers.
          </P>

          <H2>2. How we use your data</H2>
          <ul className="list-disc list-outside pl-5 text-[var(--inmind-text-secondary)] text-[15px] leading-relaxed">
            <Li>To sign you in and keep you signed in.</Li>
            <Li>To show you the daily card and preserve your notes across devices.</Li>
            <Li>To send messages you write in &quot;Think&quot; to a large-language-model provider so the AI can respond, and to save the conversation so it stays in context for you.</Li>
            <Li>To prevent brute-force sign-in attempts and other abuse (rate limiting).</Li>
            <Li>To send transactional emails related to your account (password reset, security alerts). We do <b>not</b> send marketing emails without your opt-in.</Li>
            <Li>To compute your personal statistics (streak, cards reviewed) that only you can see.</Li>
          </ul>
          <P className="mt-3">
            We do <b>not</b> sell your data. We do <b>not</b> use your notes or Think conversations to train third-party AI models.
          </P>

          <H2>3. Who we share data with</H2>
          <P>We share data only with service providers we need to run InThink.</P>
          <ul className="list-disc list-outside pl-5 text-[var(--inmind-text-secondary)] text-[15px] leading-relaxed">
            <Li><b>MongoDB Atlas</b> (or equivalent) — encrypted database storage of your account, notes, cards, and chat history.</Li>
            <Li><b>Google Sign-In</b> — only if you choose to sign in with Google; receives your Google email and public profile to authenticate you.</Li>
            <Li><b>OpenAI / Anthropic / Google (via the Emergent LLM proxy)</b> — the individual messages you send to Think and the current card or note they relate to, so the AI can respond.</Li>
            <Li><b>Cloud hosting</b> — currently Emergent — encrypted server logs and application traffic.</Li>
          </ul>
          <P className="mt-3">We do not share your data with advertisers, data brokers, or analytics networks.</P>

          <H2>4. Where your data is stored</H2>
          <P>
            Your data may be transferred to and processed in the United States or other countries where our
            providers operate. We rely on standard contractual clauses and provider certifications as the
            legal basis for these transfers.
          </P>

          <H2>5. How long we keep your data</H2>
          <ul className="list-disc list-outside pl-5 text-[var(--inmind-text-secondary)] text-[15px] leading-relaxed">
            <Li>Active accounts: as long as your account exists.</Li>
            <Li>After deletion: we remove your personal data within <b>30 days</b>, except records we must keep by law for up to 90 days.</Li>
            <Li>Server logs: rotated within 30 days.</Li>
            <Li>Rate-limit counters: rotated within 24 hours.</Li>
          </ul>

          <H2>6. Your rights and choices</H2>
          <ol className="list-decimal list-outside pl-5 text-[var(--inmind-text-secondary)] text-[15px] leading-relaxed">
            <Li><b>Access</b> your data — Preferences inside the app.</Li>
            <Li><b>Correct</b> your data — Preferences inside the app.</Li>
            <Li><b>Export</b> your data — email us; you&apos;ll receive a machine-readable file within 30 days.</Li>
            <Li><b>Delete</b> your data — email us; we&apos;ll delete your account and content within 30 days.</Li>
            <Li><b>Withdraw consent</b> — stop using InThink at any time.</Li>
            <Li><b>Complain</b> to a data-protection authority in your country.</Li>
          </ol>
          <P className="mt-3">
            If you sign in with Google, you can revoke InThink&apos;s access at{" "}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-[var(--inmind-primary)] underline">
              myaccount.google.com/permissions
            </a>.
          </P>

          <H2>7. Children</H2>
          <P>
            InThink is not directed to children under 13 (or under 16 in the European Economic Area). We do
            not knowingly collect data from children in those age groups. If you believe a child has
            provided us data, email chaudharyhardik1106@gmail.com and we will delete it promptly.
          </P>

          <H2>8. Security</H2>
          <ul className="list-disc list-outside pl-5 text-[var(--inmind-text-secondary)] text-[15px] leading-relaxed">
            <Li>Passwords are hashed with <b>bcrypt</b> — we never store plain text passwords.</Li>
            <Li>Sessions use <b>JSON Web Tokens</b> with a server-side secret.</Li>
            <Li>All traffic is served over <b>HTTPS</b>.</Li>
            <Li>We enforce <b>rate limits</b> on sign-in, register, and AI endpoints.</Li>
            <Li>Access to production systems is limited to authorised administrators.</Li>
          </ul>

          <H2>9. Push notifications</H2>
          <P>
            If you enable push notifications, we store the device subscription token needed to deliver them.
            Turning notifications off in your device settings stops us from sending them; the token is
            removed within 30 days of your last use.
          </P>

          <H2>10. Cookies</H2>
          <P>InThink uses a small number of cookies and browser-storage entries strictly required to keep
            you signed in. We do not use advertising or analytics cookies.</P>

          <H2>11. Third-party links</H2>
          <P>
            The app may contain links to external websites. This policy does not apply to those sites; please read their own policies.
          </P>

          <H2>12. Changes to this policy</H2>
          <P>
            If we materially change how we handle your data, we will update this page and change the
            &quot;Last updated&quot; date at the top. For significant changes we will also notify you inside the
            app or by email.
          </P>

          <H2>13. Contact</H2>
          <P>
            <b>Hardik Chaudhary — InThink</b>
            <br />
            Email:{" "}
            <a href="mailto:chaudharyhardik1106@gmail.com" className="text-[var(--inmind-primary)] underline">
              chaudharyhardik1106@gmail.com
            </a>
            <br />
            App: <a href="https://inthink.world" className="text-[var(--inmind-primary)] underline">https://inthink.world</a>
          </P>
        </div>
      </main>

      <footer className="border-t border-black/5 mt-12 py-8">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--inmind-muted)]">
          <span>© {new Date().getFullYear()} InThink</span>
          <div className="flex items-center gap-4">
            <Link to="/support" className="hover:text-[var(--inmind-text)]">Support</Link>
            <Link to="/privacy" className="hover:text-[var(--inmind-text)]">Privacy</Link>
            <Link to="/" className="hover:text-[var(--inmind-text)]">Open app</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
